import { useEffect, useState, useCallback } from "react";
import { Plus, ArrowLeft, ArrowUp, ArrowDown, Pencil, Trash2, ExternalLink, Images, MapPin } from "lucide-react";
import { toast } from "sonner";
import { ConfirmDialog } from "./AdminProductParts";
import { AdminProjectForm, toForm } from "./AdminProjectForm";
import { AdminProjectPhotos } from "./AdminProjectPhotos";
import { periodLabel } from "../components/LavoriRealizzati";
import { listProjects, createProject, updateProject, setProjectStatus, deleteProject, reorderProjects, projectPhotoUrl, formatApiError } from "../lib/adminApi";

const iconBtn = "rounded-lg p-2.5 text-[#4F5D70] hover:bg-slate-100 disabled:opacity-30 disabled:hover:bg-transparent";

const StatusBadge = ({ status }) => (
    <span data-testid="project-status-badge" className={`rounded-full px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider ${status === "published" ? "bg-emerald-100 text-emerald-800" : "bg-slate-200 text-slate-700"}`}>{status === "published" ? "Pubblicato" : "Bozza"}</span>
);

const coverOf = (p) => p.photos.find((f) => f.id === p.cover_photo_id && f.accepted) || p.photos.find((f) => f.accepted);

// Barra di pubblicazione: spiega cosa manca e permette Pubblica / Torna in bozza
const PublishBar = ({ project, form, onChanged }) => {
    const [busy, setBusy] = useState(false);
    const hasPhoto = project.photos.some((f) => f.accepted);
    const unsavedConsent = form.consent !== project.consent;
    const missing = [!project.consent && "spunta e salva il consenso del cliente", !hasPhoto && "aggiungi almeno una foto inclusa"].filter(Boolean);
    const toggle = async () => {
        setBusy(true);
        try {
            await setProjectStatus(project.id, project.status === "published" ? "draft" : "published");
            toast.success(project.status === "published" ? "Lavoro tornato in bozza: non è più visibile sul sito" : "Lavoro pubblicato: ora è visibile sul sito e nella sitemap");
            onChanged();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setBusy(false);
        }
    };
    return (
        <div className="flex flex-col gap-3 rounded-2xl border border-slate-200 bg-white p-4 sm:flex-row sm:items-center sm:justify-between" data-testid="project-publish-bar">
            <div className="text-sm">
                <div className="flex items-center gap-2"><StatusBadge status={project.status} /><span className="text-[#4F5D70]">/realizzazioni/{project.slug}</span></div>
                {project.status !== "published" && missing.length > 0 && <p className="mt-1.5 text-xs text-amber-700" data-testid="project-publish-missing">Per pubblicare: {missing.join(" e ")}.</p>}
                {unsavedConsent && <p className="mt-1.5 text-xs text-amber-700">Hai modificato il consenso: salva il modulo prima di pubblicare.</p>}
            </div>
            <div className="flex flex-wrap gap-2">
                {project.status === "published" && <a href={`/realizzazioni/${project.slug}`} target="_blank" rel="noopener noreferrer" data-testid="project-view-site" className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 px-4 py-2.5 text-xs font-bold text-[#0B1F3A] hover:bg-slate-50"><ExternalLink className="h-3.5 w-3.5" /> Vedi sul sito</a>}
                <button type="button" onClick={toggle} disabled={busy || (project.status !== "published" && missing.length > 0)} data-testid="project-publish-toggle" className={`rounded-full px-5 py-2.5 text-xs font-bold text-white disabled:opacity-50 ${project.status === "published" ? "bg-slate-600 hover:bg-slate-700" : "bg-[#E30613] hover:bg-[#C20410]"}`}>
                    {busy ? "Attendere…" : project.status === "published" ? "Torna in bozza (nascondi)" : "Pubblica sul sito"}
                </button>
            </div>
        </div>
    );
};

const ProjectEditor = ({ initial, products, onBack }) => {
    const [project, setProject] = useState(initial);
    const [form, setForm] = useState(toForm(initial));
    const [busy, setBusy] = useState(false);
    const reload = useCallback(async () => {
        const all = await listProjects();
        const fresh = all.find((p) => p.id === project?.id);
        if (fresh) setProject(fresh);
    }, [project?.id]);

    const save = async (override) => {
        setBusy(true);
        const body = { ...form, ...(override || {}) };
        try {
            const saved = project ? await updateProject(project.id, body) : await createProject(body);
            setProject(saved);
            setForm(toForm(saved));
            toast.success(project ? "Lavoro salvato" : "Lavoro creato: ora aggiungi le foto");
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="mt-8 space-y-6" data-testid="project-editor">
            <button type="button" onClick={onBack} data-testid="project-back" className="inline-flex items-center gap-2 text-sm font-bold text-[#0B1F3A] hover:text-[#E30613]"><ArrowLeft className="h-4 w-4" /> Tutti i lavori</button>
            {project && <PublishBar project={project} form={form} onChanged={reload} />}
            <section className="rounded-3xl border border-slate-200 bg-white p-5 sm:p-7">
                <h2 className="font-display text-xl font-extrabold text-[#0B1F3A]">{project ? "Dati del lavoro" : "Nuovo lavoro"}</h2>
                <div className="mt-5"><AdminProjectForm form={form} setForm={setForm} products={products} onSubmit={() => save()} busy={busy} isNew={!project} /></div>
            </section>
            {project && (
                <section className="rounded-3xl border border-slate-200 bg-white p-5 sm:p-7">
                    <h2 className="font-display text-xl font-extrabold text-[#0B1F3A]">Foto ({project.photos.length})</h2>
                    <p className="mt-1 text-sm text-[#4F5D70]">La prima foto (o quella scelta come copertina) compare nella scheda in home. Sul sito vengono pubblicate solo le foto incluse, nella versione sfocata.</p>
                    <div className="mt-5"><AdminProjectPhotos project={project} onChanged={reload} onSetCover={(id) => save({ cover_photo_id: id })} /></div>
                </section>
            )}
        </div>
    );
};

const ProjectRow = ({ p, index, total, onEdit, onMove, onDeleted }) => {
    const [confirm, setConfirm] = useState(false);
    const [busy, setBusy] = useState(false);
    const cover = coverOf(p);
    const remove = async () => {
        setBusy(true);
        try { await deleteProject(p.id); toast.success("Lavoro eliminato"); setConfirm(false); onDeleted(); } catch (err) { toast.error(formatApiError(err)); } finally { setBusy(false); }
    };
    return (
        <li className="rounded-2xl border border-slate-200 bg-white p-3" data-testid={`project-row-${p.id}`}>
            <div className="flex items-start gap-3">
                <button type="button" onClick={onEdit} className="h-20 w-24 shrink-0 overflow-hidden rounded-xl bg-slate-100" aria-label="Apri">
                    {cover ? <img src={projectPhotoUrl(cover)} alt="" className="h-full w-full object-cover" /> : <span className="flex h-full items-center justify-center text-slate-300"><Images className="h-6 w-6" /></span>}
                </button>
                <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2"><StatusBadge status={p.status} /><span className="text-[10px] font-bold uppercase tracking-wider text-[#E30613]">{p.division}</span></div>
                    <button type="button" onClick={onEdit} className="mt-1 block text-left text-sm font-bold leading-snug text-[#0B1F3A] hover:text-[#E30613]" data-testid={`project-row-title-${p.id}`}>{p.title}</button>
                    <p className="mt-1 flex flex-wrap items-center gap-x-2 text-xs text-[#4F5D70]"><span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3" />{[p.city, p.province && `(${p.province})`].filter(Boolean).join(" ") || "—"}</span>{p.period && <span>· {periodLabel(p.period)}</span>}<span>· {p.photos.length} foto</span>{!p.consent && <span className="font-semibold text-amber-700">· consenso mancante</span>}</p>
                </div>
            </div>
            <div className="mt-2 flex flex-wrap items-center gap-0.5 border-t border-slate-100 pt-2">
                <button type="button" onClick={() => onMove(-1)} disabled={index === 0} className={iconBtn} aria-label="Sposta su" title="Sposta su" data-testid={`project-up-${p.id}`}><ArrowUp className="h-4 w-4" /></button>
                <button type="button" onClick={() => onMove(1)} disabled={index === total - 1} className={iconBtn} aria-label="Sposta giù" title="Sposta giù" data-testid={`project-down-${p.id}`}><ArrowDown className="h-4 w-4" /></button>
                <button type="button" onClick={onEdit} className={`${iconBtn} inline-flex items-center gap-1.5 text-xs font-bold text-[#0B1F3A]`} data-testid={`project-edit-${p.id}`}><Pencil className="h-4 w-4" /> Modifica</button>
                {p.status === "published" && <a href={`/realizzazioni/${p.slug}`} target="_blank" rel="noopener noreferrer" className={iconBtn} aria-label="Vedi sul sito" title="Vedi sul sito" data-testid={`project-open-${p.id}`}><ExternalLink className="h-4 w-4" /></a>}
                <button type="button" onClick={() => setConfirm(true)} className="ml-auto rounded-lg p-2.5 text-[#E30613] hover:bg-red-50" aria-label="Elimina" title="Elimina" data-testid={`project-delete-${p.id}`}><Trash2 className="h-4 w-4" /></button>
            </div>
            <ConfirmDialog open={confirm} onOpenChange={setConfirm} title={`Eliminare "${p.title}"?`} description="Il lavoro e tutte le sue foto saranno eliminati definitivamente e la pagina sparirà dal sito." onConfirm={remove} busy={busy} testId={`project-delete-dialog-${p.id}`} />
        </li>
    );
};

export const AdminRealizzazioni = ({ products }) => {
    const [items, setItems] = useState(null);
    const [filter, setFilter] = useState("all");
    const [editing, setEditing] = useState(undefined); // undefined = elenco, null = nuovo, oggetto = modifica
    const load = useCallback(() => listProjects().then(setItems).catch((e) => toast.error(formatApiError(e))), []);
    useEffect(() => { load(); }, [load]);

    if (editing !== undefined) return <ProjectEditor initial={editing} products={products} onBack={() => { setEditing(undefined); load(); }} />;

    const list = (items || []).filter((p) => filter === "all" || p.status === filter);
    const move = async (i, dir) => {
        const ids = items.map((p) => p.id);
        const a = ids.indexOf(list[i].id), b = ids.indexOf(list[i + dir].id);
        [ids[a], ids[b]] = [ids[b], ids[a]];
        try { await reorderProjects(ids); load(); } catch (err) { toast.error(formatApiError(err)); }
    };
    return (
        <div className="mt-8" data-testid="admin-realizzazioni">
            <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex flex-wrap gap-2" data-testid="project-filter">
                    {[["all", "Tutti"], ["published", "Pubblicati"], ["draft", "Bozze"]].map(([k, l]) => (
                        <button key={k} type="button" onClick={() => setFilter(k)} data-testid={`project-filter-${k}`} className={`rounded-full px-4 py-2 text-xs font-bold ${filter === k ? "bg-[#0B1F3A] text-white" : "bg-white text-[#0B1F3A] hover:bg-slate-100"}`}>{l}{items && ` (${items.filter((p) => k === "all" || p.status === k).length})`}</button>
                    ))}
                </div>
                <button type="button" onClick={() => setEditing(null)} data-testid="project-new" className="inline-flex items-center gap-2 rounded-full bg-[#E30613] px-5 py-3 text-sm font-bold text-white hover:bg-[#C20410]"><Plus className="h-4 w-4" /> Nuovo lavoro</button>
            </div>
            {items === null ? (
                <p className="mt-6 text-sm text-[#4F5D70]">Caricamento…</p>
            ) : list.length === 0 ? (
                <div className="mt-6 rounded-3xl border border-dashed border-slate-300 bg-white p-10 text-center" data-testid="project-empty">
                    <Images className="mx-auto h-8 w-8 text-slate-300" />
                    <p className="mt-3 text-sm font-bold text-[#0B1F3A]">{filter === "all" ? "Nessun lavoro ancora" : "Nessun lavoro in questo stato"}</p>
                    <p className="mt-1 text-xs text-[#4F5D70]">Crea un lavoro, carica le foto (volti e targhe vengono sfocati automaticamente), spunta il consenso del cliente e pubblica: la pagina compare subito sul sito e nella sitemap.</p>
                </div>
            ) : (
                <ul className="mt-6 space-y-3" data-testid="project-list">
                    {list.map((p, i) => <ProjectRow key={p.id} p={p} index={i} total={list.length} onEdit={() => setEditing(p)} onMove={(d) => move(i, d)} onDeleted={load} />)}
                </ul>
            )}
        </div>
    );
};
